import OverviewBg from '../../../assets/images/tatvacare/projectoverviewbg.webp'
import solution from '../../../assets/images/tatvacare/solution.webp'
import problem from '../../../assets/images/tatvacare/problem.webp'
import illustration from '../../../assets/images/tatvacare/illustration.gif'
import Constants from '../../../app/utils/constants'
 import { motion } from 'framer-motion'
const bullets = [
  'Medical information is scattered across multiple sources',
  'Searching takes time during patient interaction',
  'Existing tools lack contextual understanding',
  'Trust in AI-generated responses remains a concern',
]

const deliveredSections = [
  {
    title: 'The Problem',
    image: problem,
    description:
      'In fast-paced clinical environments, doctors face constant pressure to make accurate decisions quickly. However:',
    bullets,
    result:
      'This creates a gap between speed, accuracy, and reliability.',
  },

  {
    title: 'The Solution',
    image: solution, 
    description:
      'We designed Tattva Care as an AI-powered medical assistant that delivers:',
    bullets: [
      'Instant, structured answers',
      'Context-aware medical insights',
      'Verified, source-backed information',
    ],
    result:
      'All within a clean, distraction-free interface built for real clinical use.',
  },
]


const TatvacareProjectOverview = () => {
  return (
    <>
      {/* ── SECTION 1: PROJECT OVERVIEW ── */}
     

<section className="w-full bg-[#4a4ad575] px-4 py-7 md:px-10 md:py-10">

  <div className="mx-auto grid w-full grid-cols-2 gap-4 md:flex md:flex-row md:items-stretch md:gap-8 lg:gap-20">

    {Constants.statItems.map((item, index) => (
      <motion.div
        key={item.num}

        initial={{
          opacity: 0,
          scale: 0.6,
        }}

        whileInView={{
          opacity: 1,
          scale: 1,
        }}

        viewport={{
          once: false,
          amount: 0.3,
        }}

        transition={{
          duration: 0.6,
          delay: index * 0.15,
          ease: 'easeOut',
        }}

        className="
          flex
          h-full
          min-h-[200px]
          flex-1
          flex-col
          items-center
          text-center

          md:min-h-[170px]
          lg:min-h-[290px]
        "
      >

        {/* Number Circle */}
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white shadow-lg md:h-18 md:w-18 lg:h-20 lg:w-20 xl:h-24 xl:w-24">

          <span className="bg-gradient-to-r from-[#4B4AD5] to-[#A461D8] bg-clip-text text-2xl font-gilroy-bold text-transparent md:text-3xl 2xl:text-4xl">
            {item.num}
          </span>

        </div>

        {/* Heading */}
        <h3 className="mt-2 font-gilroy-bold text-xl text-white md:text-2xl xl:text-3xl">
          {item.heading}
        </h3>

        {/* Content */}
        <p className="mt-2 font-gilroy text-base leading-relaxed text-white/80 md:max-w-sm md:text-lg xl:text-xl 2xl:text-2xl">

          {Array.isArray(item.data)
            ? item.data.map((line, idx) => (
                <span key={idx}>
                  {line}
                  <br />
                </span>
              ))
            : item.data}

        </p>

      </motion.div>
    ))}

  </div>

</section>
    

      {/* ── ILLUSTRATION BRIDGE — sits between the two sections ── */}
      <div className="relative top-10 z-20 w-full pointer-events-none -ml-[35px] md:ml-0 -mt-[70px] md:-mt-[120px] -mb-[50px] md:-mb-[50px] lg:-mb-[100px] xl:-mb-[120px]">
        <img
          src={illustration}
          alt=""
          className=" h-[110px] left-[10px] lg:h-[200px] xl:h-[220px] ml-10 w-auto object-contain md:h-[170px] 2xl:h-[240px]"
        />
      </div>

      {/* ── SECTION 2: STAT ── */}
<section className="w-full">

  <motion.div
    initial={{
      opacity: 0,
      scale: 0.7,
    }}
    whileInView={{
      opacity: 1,
      scale: 1,
    }}
    viewport={{
      once: false,
      amount: 0.3,
    }}
    transition={{
      duration: 0.8,
      ease: 'easeOut',
    }}
    className="
      relative
      flex
      min-h-[160px]
      w-full
      items-center
      justify-center
      overflow-hidden
      px-5
      py-10

      md:min-h-[220px]
      md:py-10

      lg:min-h-[300px]
      lg:py-16
    "
  >

    {/* Background */}
    <div className="absolute inset-0 bg-white" />

    <img
      src={OverviewBg}
      alt=""
      className="absolute inset-0 h-full w-full object-cover object-left"
    />

    {/* CONTENT */}
    <motion.div
      initial={{
        opacity: 0,
        y: 50,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
      }}
      viewport={{
        once: true,
      }}
      transition={{
        delay: 0.2,
        duration: 0.7,
      }}
      className="
        relative
        z-10
        mx-auto
        max-w-full
        text-center

        lg:max-w-[75%]
        xl:max-w-[80%]
      "
    >

      <h2 className="font-gilroy-bold text-2xl text-black md:text-3xl lg:text-5xl">
        Project Overview
      </h2>

      <p className="mt-4 font-gilroy-medium text-sm leading-relaxed text-black/75 md:mt-6 md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl">
        Tattva Care is a modern AI-driven healthcare platform designed to empower
        doctors with instant access to verified medical knowledge. By combining
        conversational AI with trusted clinical sources, the platform transforms
        how practitioners search, analyze, and apply medical information during
        consultations.
      </p>

    </motion.div>

  </motion.div>

</section>


      {/* ── SECTION 3: WHAT WE DELIVERED ── */}

<section className="relative w-full overflow-hidden bg-[#4a4ad575]/10 pt-1 pb-10 md:pb-15">

  {/* TOP RIGHT BLUR */}
  <div className="pointer-events-none absolute right-[-20%] top-[-25%] z-0 h-[740px] w-[880px] rounded-full border-[200px] border-[#A461D8]/25 blur-[38px]" />

  {/* BOTTOM LEFT BLUR */}
  <div className="pointer-events-none absolute bottom-[-23%] left-[-6%] z-0 h-[500px] w-[600px] rounded-full border-[300px] border-[#A461D8]/25 blur-[38px]" />

  <div className="mx-auto w-full max-w-[90%] md:px-4 xl:px-16 2xl:px-24">

    {deliveredSections.map((section, index) => {
      const reverse = index % 2 !== 0

      return (
        <div
          key={index}
          className="
            mt-7
            grid
            grid-cols-1
            items-center
            gap-4

            md:mt-10
            md:gap-8

            lg:mt-20
            lg:grid-cols-2
            lg:gap-10
          "
        >

          {/* IMAGE */}
          <motion.div
            initial={{
              opacity: 0,
              x: reverse ? 180 : -180,
              scale: 0.8,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
              scale: 1,
            }}
            viewport={{
              once: false,
              amount: 0.35,
            }}
            transition={{
              duration: 0.9,
              ease: 'easeOut',
            }}
            className={`order-1 flex justify-center ${
              reverse
                ? 'lg:order-2'
                : 'lg:order-1'
            }`}
          >

            <div className="relative flex aspect-square w-[240px] items-center justify-center overflow-hidden rounded-full md:w-[300px] lg:w-[350px] xl:w-[380px] 2xl:w-[520px]">

              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: `
                    linear-gradient(
                      180deg,
                      #c2a2dba2 0%,
                      rgba(191,152,221,.6) 28%,
                      rgba(191,152,221,.6) 55%,
                      #9f7cf1f6 60%,
                      #9570ec 99%
                    )
                  `,
                }}
              />

<motion.img
  src={section.image}
  alt={section.title}
  animate={{
    y: [18, 0, 18],
  }}
  transition={{
    duration: 3,
    repeat: Infinity,
    repeatType: 'loop',
    ease: 'easeIn',
  }}
  className="
    absolute
    bottom-0
    z-10
    w-[78%]
    max-h-[82%]
    object-contain
  "
/>

            </div>

          </motion.div>

          {/* CONTENT */}
          <motion.div
            initial={{
              opacity: 0,
              x: reverse ? -180 : 180,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            viewport={{
              once: false,
              amount: 0.35,
            }}
            transition={{
              duration: 0.9,
              delay: 0.15,
              ease: 'easeOut',
            }}
            className={`
              order-2
              text-left
              ${
                reverse
                  ? 'lg:order-1'
                  : 'lg:order-2'
              }
            `}
          >

            {/* Heading */}
            <div className="flex items-end">

              <h2 className="shrink-0 font-gilroy-bold text-2xl text-white md:text-3xl lg:text-4xl xl:text-4xl 2xl:text-5xl">
                {section.title}
              </h2>

              <span className="mb-2 h-[2px] w-15 rounded-full bg-black md:w-32" />

            </div>

            {/* Description */}
            <p className="mt-2 font-gilroy-medium text-sm leading-normal text-white/70 md:mt-3 md:text-lg lg:text-base xl:mt-5 xl:text-lg xl:leading-relaxed 2xl:text-2xl">
              {section.description}
            </p>

            {/* Bullets */}
            <ul className="mt-2 space-y-1 text-left font-gilroy-medium text-base text-white md:mt-4 md:text-lg lg:text-lg xl:mt-8 xl:space-y-3 xl:text-xl 2xl:text-2xl">

              {section.bullets.map((line) => (
                <li key={line} className="flex gap-3">

                  <span className="mt-[4%] h-1 w-1 shrink-0 rounded-full bg-white md:mt-[1.5%] lg:mt-[3%] xl:mt-[1.5%] xl:h-2 xl:w-2" />

                  <span>
                    {line}
                  </span>

                </li>
              ))}

            </ul>

            {/* Result */}
            <p className="mt-4 font-gilroy-medium text-sm leading-normal text-white/70 md:text-lg md:leading-relaxed lg:text-base xl:mt-8 xl:text-lg 2xl:text-2xl">
              {section.result}
            </p>

          </motion.div>

        </div>
      )
    })}

  </div>

</section>
    </>
  )
}

export default TatvacareProjectOverview